import { useCallback } from "react";
import { Button, Callout, Text, Flex, IconButton } from "@radix-ui/themes";
import { PlusIcon, Cross2Icon, InfoCircledIcon } from "@radix-ui/react-icons";
import type { EnrichmentOption } from "@/v2/entities/enrichment/Enrichment.model";
import styles from "./enrichmentForm.module.scss";

// ai-v2 only treats an option named exactly "None" or "Other" as the no-match answer
const CATCH_ALL_OPTION_NAMES = ["none", "other"];

interface ClassificationOptionsFormProps {
  options: EnrichmentOption[];
  onOptionsChange: (options: EnrichmentOption[]) => void;
}

function ClassificationOptionsForm({
  options,
  onOptionsChange,
}: ClassificationOptionsFormProps) {
  const handleNameChange = useCallback(
    (index: number, name: string) => {
      const updated = options.map((opt, i) =>
        i === index
          ? { ...opt, name, value: name.toLowerCase().replace(/\s+/g, "_") }
          : opt
      );
      onOptionsChange(updated);
    },
    [options, onOptionsChange]
  );

  const handleBriefChange = useCallback(
    (index: number, brief: string) => {
      const updated = options.map((opt, i) =>
        i === index ? { ...opt, brief } : opt
      );
      onOptionsChange(updated);
    },
    [options, onOptionsChange]
  );

  const handleAdd = useCallback(() => {
    onOptionsChange([...options, { name: "", value: "", brief: "" }]);
  }, [options, onOptionsChange]);

  const handleRemove = useCallback(
    (index: number) => {
      onOptionsChange(options.filter((_, i) => i !== index));
    },
    [options, onOptionsChange]
  );

  const hasCatchAllOption = options.some((option) =>
    CATCH_ALL_OPTION_NAMES.includes(option.name.trim().toLowerCase())
  );

  return (
    <Flex direction="column" gap="3" width="100%">
      <div className={styles.optionsSectionHeader}>
        <Text size="2" weight="medium" color="gray">
          Options
        </Text>
      </div>

      <div className={styles.optionsTable}>
        <div className={styles.optionsTableHeader}>
          <Text size="1" color="gray" className={styles.optionsTableColName}>Name</Text>
          <Text size="1" color="gray" className={styles.optionsTableColBrief}>Description</Text>
          <div className={styles.optionsTableColAction} />
        </div>
        {options.map((option, index) => (
          <div key={index} className={styles.optionsTableRow}>
            <input
              type="text"
              value={option.name}
              onChange={(e) => handleNameChange(index, e.target.value)}
              placeholder="Option name"
              className={styles.optionsTableColName}
            />
            <input
              type="text"
              value={option.brief || ""}
              onChange={(e) => handleBriefChange(index, e.target.value)}
              placeholder="IF condition..."
              className={styles.optionsTableColBrief}
            />
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              onClick={() => handleRemove(index)}
              className={styles.optionsTableColAction}
            >
              <Cross2Icon />
            </IconButton>
          </div>
        ))}
      </div>

      {options.length > 0 && !hasCatchAllOption && (
        <Callout.Root color="amber" variant="soft" size="1">
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>
            If some tickets won't match any option, add an option named exactly "None" or "Other".
            Without it, the AI always picks the closest option. Names like "No request" won't work.
          </Callout.Text>
        </Callout.Root>
      )}

      <Button
        size="1"
        variant="ghost"
        color="gray"
        onClick={handleAdd}
        className={styles.addOptionButton}
      >
        <PlusIcon />
        Add option
      </Button>
    </Flex>
  );
}

export default ClassificationOptionsForm;
